/**
 * Search component.
 * @module components/theme/Search/Search
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import { createPortal } from 'react-dom';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { searchContent } from '@plone/volto/actions/search/search';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';

import ResultadosBusca from '../../../../components/busca/ResultadosBusca';
import {
  OPCOES_DA_RAIZ,
  TODAS_AS_ABAS,
  caminhosDaRaiz,
  lerUrl,
  montarUrl,
  opcoesDaBusca,
  opcoesDaContagem,
  secoesDoMenu,
} from '../../../../components/busca/busca';

// A busca principal fica no espaço global da busca, como no core; a listagem
// da raiz (que dá os caminhos) e as contagens das abas, em chaves próprias.
const CHAVES = {
  busca: undefined,
  raiz: 'agsusBuscaRaiz',
  contagens: Object.fromEntries(
    TODAS_AS_ABAS.map((a) => [a.id, `agsusBuscaContagem_${a.id}`]),
  ),
};

// Despacha a busca e as contagens com os caminhos da raiz, pedindo a raiz antes
// quando ela ainda não está na loja. Se a raiz falhar, busca sem restrição.
// Serve à página e ao servidor: `buscar(opcoes, chave)` despacha uma busca.
const buscarTudo = ({ buscar, chaves, raiz, params, contar }) => {
  const despachar = (caminhos) =>
    Promise.all([
      buscar(opcoesDaBusca({ ...params, caminhos }), chaves.busca),
      ...(contar
        ? TODAS_AS_ABAS.map((a) =>
            buscar(
              opcoesDaContagem({
                termo: params.termo,
                assunto: params.assunto,
                aba: a.id,
                caminhos,
              }),
              chaves.contagens[a.id],
            ),
          )
        : []),
    ]);
  const caminhos = raiz?.loaded
    ? Promise.resolve(caminhosDaRaiz(raiz.items))
    : Promise.resolve(buscar(OPCOES_DA_RAIZ, chaves.raiz)).then(
        (r) => caminhosDaRaiz(r?.items),
        () => [],
      );
  return caminhos.then(despachar).catch(() => null);
};

class Search extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(PropTypes.object),
    search: PropTypes.object,
    raiz: PropTypes.object,
    contagens: PropTypes.objectOf(PropTypes.number),
    secoes: PropTypes.arrayOf(PropTypes.object),
    pathname: PropTypes.string.isRequired,
  };

  static defaultProps = {
    items: [],
    search: {},
    raiz: null,
    contagens: {},
    secoes: [],
  };

  state = { isClient: false };

  componentDidMount() {
    this.buscar();
    this.setState({ isClient: true });
  }

  componentDidUpdate(anteriores) {
    if (anteriores.location.search !== this.props.location.search) {
      this.buscar();
    }
  }

  buscar = () => {
    const params = lerUrl(this.props.location.search);
    if (!params.termo && !params.assunto) return;
    // As contagens só dependem do termo e do assunto (os caminhos vêm da raiz).
    const procura = `${params.termo}|${params.assunto}`;
    const contar = procura !== this.termoContado;
    this.termoContado = procura;
    buscarTudo({
      buscar: (opcoes, chave) =>
        this.props.dispatch(searchContent('', opcoes, chave)),
      chaves: CHAVES,
      raiz: this.props.raiz,
      params,
      contar,
    });
  };

  irPara = (params) => {
    const atual = lerUrl(this.props.location.search);
    if (params.pagina !== atual.pagina && typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    this.props.history.push(montarUrl(params));
  };

  render() {
    const params = lerUrl(this.props.location.search);
    return (
      <>
        <Helmet
          title={
            params.termo
              ? `Busca: ${params.termo}`
              : params.assunto
                ? `Assunto: ${params.assunto}`
                : 'Busca'
          }
        />
        <ResultadosBusca
          {...params}
          total={this.props.search?.total}
          itens={this.props.items}
          carregando={!!this.props.search?.loading}
          contagens={this.props.contagens}
          secoes={this.props.secoes}
          irPara={this.irPara}
        />
        {this.state.isClient &&
          createPortal(
            <Toolbar
              pathname={this.props.pathname}
              hideDefaultViewButtons
              inner={<span />}
            />,
            document.getElementById('toolbar'),
          )}
      </>
    );
  }
}

const doEstado = (state, props) => {
  const sub = state.search?.subrequests || {};
  const contagens = {};
  TODAS_AS_ABAS.forEach((a) => {
    const c = sub[CHAVES.contagens[a.id]];
    if (c?.loaded && !c.loading) contagens[a.id] = c.total;
  });
  return {
    items: state.search.items,
    search: state.search,
    raiz: sub[CHAVES.raiz] || null,
    contagens,
    secoes: secoesDoMenu(state.navigation?.items),
    pathname: props.location.pathname,
  };
};

const porDespacho = (dispatch) => ({ dispatch });

export const __test__ = connect(doEstado, porDespacho)(Search);

export default compose(
  connect(doEstado, porDespacho),
  asyncConnect([
    // A chave não pode ser "search": o asyncConnect injeta o resultado da promessa
    // numa prop com o nome da chave, e ela passava por cima do estado da busca (o
    // servidor mostrava "Nenhum resultado" e, com erro na busca, a página caía).
    // E só busca no servidor: no navegador o asyncConnect roda de novo a cada
    // mudança de endereço, e a página já busca sozinha; cada busca saía em dobro.
    {
      key: 'agsusBusca',
      promise: ({ location, store: { dispatch, getState } }) => {
        const params = lerUrl(location.search);
        if (!__SERVER__ || !(params.termo || params.assunto)) {
          return Promise.resolve();
        }
        return buscarTudo({
          buscar: (opcoes, chave) => dispatch(searchContent('', opcoes, chave)),
          chaves: CHAVES,
          raiz: getState().search?.subrequests?.[CHAVES.raiz],
          params,
          contar: true,
        });
      },
    },
  ]),
)(Search);
